import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Grid, Label } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { API, showError, showSuccess } from '../../helpers';
import RulesTable from './RulesTable';
import EditRuleModal from './EditRuleModal';
import TestPanel from './TestPanel';

const ErrorMappingPage = () => {
  const { t } = useTranslation();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [importingBuiltin, setImportingBuiltin] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const summary = useMemo(() => {
    const builtin = rules.filter((rule) =>
      String(rule.name || '').startsWith('builtin:')
    ).length;
    const enabled = rules.filter((rule) => rule.enabled).length;
    return {
      total: rules.length,
      enabled,
      builtin,
    };
  }, [rules]);

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/admin/error-mapping-rules');
      const { success, message, data } = res.data;
      if (success) {
        setRules(Array.isArray(data) ? data : []);
      } else {
        showError(message || t('error_mapping.messages.load_failed'));
      }
    } catch (error) {
      showError(error.message || t('error_mapping.messages.load_failed'));
    }
    setLoading(false);
  }, [t]);

  useEffect(() => {
    loadRules().then();
  }, [loadRules]);

  const openCreateModal = () => {
    setEditingRule(null);
    setModalOpen(true);
  };

  const openEditModal = (rule) => {
    setEditingRule(rule);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingRule(null);
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      const request = payload.id
        ? API.put(`/api/admin/error-mapping-rules/${payload.id}`, payload)
        : API.post('/api/admin/error-mapping-rules', payload);
      const res = await request;
      const { success, message } = res.data;
      if (!success) {
        showError(message || t('error_mapping.messages.save_failed'));
        return false;
      }
      showSuccess(
        payload.id
          ? t('error_mapping.messages.update_success')
          : t('error_mapping.messages.create_success')
      );
      setModalOpen(false);
      setEditingRule(null);
      await loadRules();
      return true;
    } catch (error) {
      showError(error.message || t('error_mapping.messages.save_failed'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rule) => {
    try {
      const res = await API.delete(`/api/admin/error-mapping-rules/${rule.id}`);
      const { success, message } = res.data;
      if (!success) {
        showError(message || t('error_mapping.messages.delete_failed'));
        return;
      }
      showSuccess(t('error_mapping.messages.delete_success'));
      await loadRules();
    } catch (error) {
      showError(error.message || t('error_mapping.messages.delete_failed'));
    }
  };

  const handleReload = async () => {
    setReloading(true);
    try {
      const res = await API.post('/api/admin/error-mapping-rules/reload');
      const { success, message } = res.data;
      if (!success) {
        showError(message || t('error_mapping.messages.reload_failed'));
        return;
      }
      showSuccess(t('error_mapping.messages.reload_success'));
      await loadRules();
    } catch (error) {
      showError(error.message || t('error_mapping.messages.reload_failed'));
    } finally {
      setReloading(false);
    }
  };

  const handleImportBuiltin = async () => {
    setImportingBuiltin(true);
    try {
      const res = await API.post('/api/admin/error-mapping-rules/import-builtin');
      const { success, message, data } = res.data;
      if (!success) {
        showError(message || t('error_mapping.messages.import_failed'));
        return;
      }
      showSuccess(
        t('error_mapping.messages.import_success', {
          imported: data?.imported ?? 0,
          skipped: data?.skipped ?? 0,
        })
      );
      await loadRules();
    } catch (error) {
      showError(error.message || t('error_mapping.messages.import_failed'));
    } finally {
      setImportingBuiltin(false);
    }
  };

  return (
    <div className='dashboard-container'>
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={16}>
            <Card fluid className='chart-card'>
              <Card.Content>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '12px',
                    flexWrap: 'wrap',
                    marginBottom: '16px',
                  }}
                >
                  <div style={{ minWidth: '280px', flex: '1 1 420px' }}>
                    <Card.Header className='header'>
                      {t('error_mapping.title', { defaultValue: '错误码映射' })}
                    </Card.Header>
                    <div
                      style={{
                        color: '#666',
                        fontSize: '13px',
                        lineHeight: 1.7,
                        marginTop: '8px',
                      }}
                    >
                      {t('error_mapping.description', {
                        defaultValue:
                          '先定义匹配条件，再定义命中后的内部错误码和展示消息。内置规则可导入后再按需微调。',
                      })}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        flexWrap: 'wrap',
                        marginTop: '12px',
                      }}
                    >
                      <Label basic color='blue'>
                        {t('error_mapping.summary.total', {
                          defaultValue: '规则 {{count}}',
                          count: summary.total,
                        })}
                      </Label>
                      <Label basic color='green'>
                        {t('error_mapping.summary.enabled', {
                          defaultValue: '启用 {{count}}',
                          count: summary.enabled,
                        })}
                      </Label>
                      <Label basic color='grey'>
                        {t('error_mapping.summary.builtin', {
                          defaultValue: '内置 {{count}}',
                          count: summary.builtin,
                        })}
                      </Label>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      flex: '1 1 320px',
                    }}
                  >
                    <Button
                      primary
                      style={{ minWidth: '108px' }}
                      onClick={openCreateModal}
                    >
                      {t('error_mapping.buttons.add', { defaultValue: '新增规则' })}
                    </Button>
                    <Button
                      style={{ minWidth: '108px' }}
                      loading={importingBuiltin}
                      onClick={handleImportBuiltin}
                    >
                      {t('error_mapping.buttons.import_builtin', {
                        defaultValue: '导入内置规则',
                      })}
                    </Button>
                    <Button
                      style={{ minWidth: '108px' }}
                      loading={reloading}
                      onClick={handleReload}
                    >
                      {t('error_mapping.buttons.reload', { defaultValue: '手动刷新' })}
                    </Button>
                  </div>
                </div>
                <RulesTable
                  rules={rules}
                  loading={loading}
                  onEdit={openEditModal}
                  onDelete={handleDelete}
                />
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={16}>
            <TestPanel />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <EditRuleModal
        open={modalOpen}
        loading={saving}
        rule={editingRule}
        onClose={closeModal}
        onSave={handleSave}
      />
    </div>
  );
};

export default ErrorMappingPage;
